import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { GridList, GridListItem } from "react-aria-components"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import TextField from "@/globalComponents/molecules/TextField"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizeLicensePlate } from "@/utilities/normalizers"

import { ConfirmVehicleModal } from "./ConfirmVehicleModal"
import styles from "./reservationModals.module.css"

interface Props {
  isOpen: boolean
  onOpenChange: () => void
  onSelectPlate?: (plate: string) => void
}

export function VehicleModal({
  isOpen,
  onOpenChange,
  onSelectPlate,
}: Props) {
  const { data: me } = useQuery(meQueryOptions())
  const savedVehicles = me?.inflowUser.savedPlates || []
  const { t } = useTranslation("reserve")
  const [manualPlate, setManualPlate] = useState("")
  const [plateError, setPlateError] = useState(false)
  const [pendingPlate, setPendingPlate] = useState("")
  const [confirmOpen, toggleConfirm] = useToggle(false)

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
        <Modal.Header>
          <Heading level={2} slot="title">
            {t("confirmation.chooseOrAddVehicle")}
          </Heading>
        </Modal.Header>

        <Modal.Body>
          {savedVehicles.length > 0 && (
            <>
              <Label>{t("confirmation.yourSavedPlates")}</Label>
              <GridList
                aria-label={t("confirmation.yourSavedPlates")}
                items={savedVehicles}
                className={styles.vehicleList}
              >
                {(vehicle) => (
                  <GridListItem
                    id={`${vehicle.plate}-${vehicle.nickname}`}
                    key={`${vehicle.plate}-${vehicle.nickname}`}
                    textValue={vehicle.nickname ? `${vehicle.nickname} (${vehicle.plate})` : vehicle.plate}
                    className="saved-vehicle-input"
                    onAction={() => handleStageConfirm(vehicle.plate)}
                  >
                    <Icon name="car" />
                    <Text as="span" className="semi-bold start">
                      {vehicle.nickname ? (
                        <>
                          {vehicle.nickname}&nbsp;
                          <Text as="span" className="regular">({vehicle.plate})</Text>
                        </>
                      ) : (
                        vehicle.plate
                      )}
                    </Text>
                    <Icon name="chevronRight" />
                  </GridListItem>
                )}
              </GridList>
            </>
          )}

          <div className="horizontal-separator-with-text">
            <Separator orientation="horizontal" />
            <Text level={3} className="bold">
              {t("confirmation.orManuallyEnter")}
            </Text>
            <Separator orientation="horizontal" />
          </div>

          <div className={styles.manualPlateRow}>
            <TextField
              value={manualPlate}
              isInvalid={plateError}
              onChange={(v) => {
                setManualPlate(normalizeLicensePlate(v))
                if (plateError) setPlateError(false)
              }}
              aria-label={t("confirmation.enterLicensePlate")}
            >
              <TextField.Input
                placeholder={t("confirmation.enterLicensePlate")}
                maxLength={12}
                style={{ textTransform: "uppercase" }}
              />
              {plateError && (
                <FieldError>{t("confirmation.plateRequired")}</FieldError>
              )}
            </TextField>
            <Button
              buttonType="default"
              onPress={handleAddPlate}
            >
              <span style={{ color: "var(--neutral-12)" }}>
                {t("confirmation.add")}
              </span>
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <ConfirmVehicleModal
        isOpen={confirmOpen}
        onOpenChange={toggleConfirm}
        plate={pendingPlate}
        onConfirm={handleConfirm}
      />
    </>
  )

  function handleAddPlate() {
    if (!manualPlate.trim()) {
      setPlateError(true)
      return
    }
    handleStageConfirm(manualPlate.trim())
  }

  function handleStageConfirm(plate: string) {
    setPendingPlate(plate)
    onOpenChange()
    toggleConfirm()
  }

  function handleConfirm() {
    onSelectPlate?.(pendingPlate)
    toggleConfirm()
    setManualPlate("")
  }
}
